import React, { useRef, useState } from 'react';
import { InfoCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { Popover, Typography } from 'antd';
import { observer } from 'mobx-react';

import QuoteStore from '../stores/QuoteStore';
import { getMissingSpecs } from '../utils/partStatus';

type Feedback = { status: 'idle' | 'loading' | 'ready' | 'error'; reasons: string[] };

const { Text } = Typography;

/** What the icon asks: why there is no automatic price at all, or why there is no Expedited or Standard one. */
export type PricingFeedbackVariant = 'manual' | 'no-expedite' | 'no-standard';

const WORDING: Record<PricingFeedbackVariant, { title: string; label: string }> = {
    manual: {
        title: "Why isn't this priced automatically?",
        label: 'Why is a manual quote needed?',
    },
    'no-expedite': {
        title: 'Why is there no Expedited price?',
        label: 'Why is there no Expedited price?',
    },
    'no-standard': {
        title: 'Why is there no Standard price?',
        label: 'Why is there no Standard price?',
    },
};

/**
 * A discreet info icon, for staff only, that loads the internal reasons why a line item is
 * not priced automatically (the same ones the Quote Tool shows) the moment it is opened.
 * Customers and guests never get the icon, and the server refuses them the reasons anyway.
 */
export const PricingFeedback = observer(
    ({ lineItemId, variant = 'manual' }: { lineItemId?: number; variant?: PricingFeedbackVariant }) => {
        const [feedback, setFeedback] = useState<Feedback>({ status: 'idle', reasons: [] });
        const latestRequest = useRef(0);

        if (!QuoteStore.for_staff || !lineItemId) return null;

        // Same evaluation as the "Pending" tag and the Continue validation: a part that still
        // has selections to make is not priced yet, so the pricing reasons would only mislead.
        const missingSpecs = getMissingSpecs(QuoteStore.errorFields ?? [], [lineItemId]);

        const load = () => {
            if (missingSpecs.length) return;
            const request = ++latestRequest.current;
            setFeedback(current => ({ status: 'loading', reasons: current.reasons }));
            QuoteStore.getPricingFeedback(lineItemId)
                .then(data => {
                    if (request === latestRequest.current) {
                        setFeedback({ status: 'ready', reasons: data?.reasons ?? [] });
                    }
                })
                .catch(() => {
                    if (request === latestRequest.current) setFeedback({ status: 'error', reasons: [] });
                });
        };

        const content = (
            <div
                data-line-item-id={lineItemId}
                data-status={feedback.status}
                data-testid="pricing-feedback-content"
                style={{ maxWidth: 360 }}
            >
                {missingSpecs.length ? (
                    <Text style={{ fontSize: 12 }}>
                        {`Select ${missingSpecs.join(', ')}. Pricing starts once the selections are made.`}
                    </Text>
                ) : feedback.status === 'error' ? (
                    <Text type="danger">Could not load the reasons.</Text>
                ) : feedback.status === 'loading' && !feedback.reasons.length ? (
                    <Text type="secondary">
                        <LoadingOutlined spin /> Loading…
                    </Text>
                ) : feedback.reasons.length ? (
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {feedback.reasons.map((reason, index) => (
                            <li key={index} data-testid="pricing-feedback-reason" style={{ fontSize: 12 }}>
                                {reason}
                            </li>
                        ))}
                    </ul>
                ) : (
                    <Text type="secondary">No reasons recorded.</Text>
                )}
            </div>
        );

        return (
            <Popover
                content={content}
                onOpenChange={open => open && load()}
                placement="right"
                title={WORDING[variant].title}
                trigger={['hover', 'click']}
            >
                <InfoCircleOutlined
                    aria-label={WORDING[variant].label}
                    data-feedback-variant={variant}
                    data-line-item-id={lineItemId}
                    data-testid="pricing-feedback"
                    onClick={event => event.stopPropagation()}
                    role="img"
                    style={{ color: '#8c8c8c', cursor: 'help', fontSize: 13, marginLeft: 6, opacity: 0.8 }}
                />
            </Popover>
        );
    }
);

export default PricingFeedback;
